import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import toast from "react-hot-toast";
import {
  MdPerson, MdWorkOutline, MdAssignment, MdUploadFile, MdInsights,
} from "react-icons/md";
import { BsDownload, BsCloudUpload } from "react-icons/bs";
import Header from "../../Header";
import LeftNavBar from "../../LeftNavBar";
import PageHeader from "../../PageHeader";
import { PageHeaderName } from "../../../StyledCss/styledComponents";
import { PrimaryButton, SecondaryButton } from "../../../StyledCss/styledbtn";
import { ErrorText } from "../../../StyledCss/styledComponents";
import {
  StyledInput, StyledInputContainer, StyledInputGroup, StyledLabel,
  StyledSelect, StyledSelectContainer, StyledSelectGroup,
} from "../../../StyledCss/styledInputs";
import { updateHiringApplication, updateHiringApplicationJson } from "../../../api/services/hroneApi";
import { getHiringListPath } from "../../../util/hiringNav";
import { usePermissions } from "../../../redux/slices/permissionsSlice";
import { FEATURE_CODES } from "../../../util/featureCodes";
import {
  EMAIL_PATTERN,
  MOBILE_PATTERN,
  getAtsMatchBadgeClass,
  getAtsScoreColor,
  isPdfResumeFile,
  isPdfResumeUrl,
  normalizeApplication,
  validateNumericField,
  validateTextField,
} from "../../../util/hiringUtils";
import "./index.css";
import "../AtsBreakdownDrawer/index.css";
import "../AddCandidateModal/index.css";

/* ─── Tabs ──────────────────────────────────────────── */

const TABS = [
  { id: "resume",   label: "Resume" },
  { id: "overview", label: "Overview" },
];

/* ─── Constants ─────────────────────────────────────── */

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const EditField = ({ k, label, type = "text", form, setField, error }) => (
  <StyledInputGroup>
    <StyledInputContainer>
      <StyledInput
        type={type}
        placeholder=" "
        value={form[k] || ""}
        onChange={(e) => setField(k, e.target.value)}
        style={{ width: "100%", ...(error ? { borderLeft: "10px solid red" } : {}) }}
      />
      <StyledLabel className="user-label">{label}</StyledLabel>
    </StyledInputContainer>
    {error && <ErrorText>{error}</ErrorText>}
  </StyledInputGroup>
);

const NUMERIC_FIELDS = [
  ["totalExperience", "Experience"],
  ["currentCTC",      "Current CTC"],
  ["expectedCTC",     "Expected CTC"],
  ["noticePeriod",    "Notice period"],
];

const TEXT_FIELDS = [
  ["firstName",       "First name",       true],
  ["lastName",        "Last name",        true],
  ["currentLocation",  "Current location", false],
  ["currentCompany",   "Current company",  false],
];

const validateEditForm = (f) => {
  const errs = {};

  TEXT_FIELDS.forEach(([key, label, required]) => {
    const err = validateTextField(f[key], label, { required });
    if (err) errs[key] = err;
  });

  if (!f.email.trim()) errs.email = "Email is required";
  else if (!EMAIL_PATTERN.test(f.email.trim())) errs.email = "Enter a valid email address";

  if (!f.mobile.trim()) errs.mobile = "Mobile number is required";
  else if (!MOBILE_PATTERN.test(f.mobile.trim())) errs.mobile = "Enter a valid mobile number";

  NUMERIC_FIELDS.forEach(([key, label]) => {
    const err = validateNumericField(f[key], label, { required: false });
    if (err) errs[key] = err;
  });

  return errs;
};

const STATUS_BG = {
  SHORTLISTED: "#fdf746",
  HIRED:       "#88E788",
  REJECTED:    "#FFA500",
};

const STATUS_TEXT = {
  SHORTLISTED: "#856404",
  HIRED:       "#155724",
  REJECTED:    "#7a3200",
};

// APPLIED uses the tenant's own primary color (light tint background / solid
// text) instead of a fixed blue, so the badge matches the org's branding —
// the other statuses stay fixed since they're semantic (yellow/green/orange).
const getStatusBg = (status, primaryColor) =>
  status === "APPLIED" ? `${primaryColor}1a` : STATUS_BG[status];

const getStatusText = (status, primaryColor) =>
  status === "APPLIED" ? primaryColor : STATUS_TEXT[status];

const STATUS_LABEL = {
  APPLIED:     "Applied",
  SHORTLISTED: "Shortlisted",
  HIRED:       "Hired",
  REJECTED:    "Rejected",
};

// PATCH /v1/hiring/jobs/{job_id}/applications/{application_id} — these fields are
// optional server-side, so each is only sent if the user actually touched it.
const NON_MANDATORY_FIELD_MAP = {
  currentLocation: "current_location",
  totalExperience: "total_experience",
  currentCompany:  "current_company",
  currentCTC:      "current_ctc",
  expectedCTC:     "expected_ctc",
  noticePeriod:    "notice_period",
};

const AVATAR_COLORS = [
  "#e86020","#4caf50","#2196f3","#9c27b0","#ff5722","#00897b","#f06292",
];

const RESUME_ACCEPT   = "application/pdf,.pdf";
const RESUME_INPUT_ID = "candidate-details-resume";

/* ─── Helpers ───────────────────────────────────────── */

const getAtsColor = getAtsScoreColor;
const getInitials    = (f, l) => `${(f||"")[0]||""}${(l||"")[0]||""}`.toUpperCase() || "?";
const getAvatarColor = (name = "") =>
  AVATAR_COLORS[(name || "A").charCodeAt(0) % AVATAR_COLORS.length];

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const [day, month, year] = dateStr.split("-");
  return `${parseInt(day, 10)} ${MONTHS[parseInt(month, 10) - 1]} ${year}`;
};

// Some records have the literal string "null"/"undefined" stored instead of a
// real empty value (bad data from an earlier save) — treat those as blank so
// the edit form shows an empty field instead of the word "null", and so
// required/format validation gives a clear "required" error instead of a
// confusing "invalid format" one.
const cleanValue = (v) => {
  const s = (v ?? "").toString().trim();
  return /^(null|undefined)$/i.test(s) ? "" : s;
};

const buildForm = (app) => ({
  firstName:       cleanValue(app?.firstName),
  lastName:        cleanValue(app?.lastName),
  email:           cleanValue(app?.email),
  mobile:          cleanValue(app?.mobile),
  currentLocation: cleanValue(app?.currentLocation),
  totalExperience: cleanValue(app?.totalExperience),
  currentCompany:  cleanValue(app?.currentCompany),
  currentCTC:      cleanValue(app?.currentCTC),
  expectedCTC:     cleanValue(app?.expectedCTC),
  noticePeriod:    cleanValue(app?.noticePeriod),
  status:          app?.status || "APPLIED",
});

/* ─── Component ─────────────────────────────────────── */

const CandidateDetailsPage = () => {
  const navigate         = useNavigate();
  const location         = useLocation();

  const { hasPermission } = usePermissions();
  const canEditCandidate = hasPermission(FEATURE_CODES.HIRING_CANDIDATE_EDIT);
  const canDownloadResume = hasPermission(FEATURE_CODES.HIRING_RESUME_DOWNLOAD);
  const canChangeCandidateStatus = hasPermission(FEATURE_CODES.HIRING_CANDIDATE_STATUS_CHANGE);

  const orgDetails   = JSON.parse(localStorage.getItem("org"));
  const primaryColor = orgDetails?.primaryColor || "#e86020";

  const passedApp      = location.state?.app;
  const returnPath     = location.state?.returnPath || getHiringListPath(location.state?.fromHiringTab);
  const fromHiringTab  = location.state?.fromHiringTab;
  const jobTitle       = location.state?.jobTitle || null;
  const jobId          = location.state?.jobId || null;
  const jobClosed      = location.state?.jobStatus === "CLOSED";
  const applicationsReturnState = fromHiringTab ? { fromHiringTab } : undefined;

  const handleBack = () => navigate(returnPath, { state: applicationsReturnState });

  const initialCandidate = passedApp;

  const startInEditMode = !!location.state?.edit;

  const [candidate, setCandidate]         = useState(initialCandidate);
  const [activeTab, setActiveTab]         = useState(startInEditMode ? "overview" : "resume");
  const [isEditing, setIsEditing]         = useState(startInEditMode);
  const [form, setForm]                   = useState(() => buildForm(initialCandidate));
  const [initialForm, setInitialForm]     = useState(() => buildForm(initialCandidate));
  const [formErrors, setFormErrors]       = useState({});
  const [resumeFile, setResumeFile]       = useState(null);
  const [resumeError, setResumeError]     = useState("");
  const [isSavingResume, setIsSavingResume] = useState(false);

  /* ── Not found ──────────────────────────────────────── */

  if (!candidate) {
    return (
      <>
        <Header />
        <div className="d-flex" style={{ "--hiring-primary": primaryColor }}>
          <LeftNavBar />
          <div
            className="page-content-wrapper"
            style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <div style={{ textAlign: "center", color: "#646569" }}>
              <p style={{ fontSize: 18, fontWeight: 500, marginBottom: 12 }}>
                Candidate not found
              </p>
              <SecondaryButton onClick={handleBack}>
                Back to Applications
              </SecondaryButton>
            </div>
          </div>
        </div>
      </>
    );
  }

  /* ── Derived values ──────────────────────────────────── */

  const atsColor  = getAtsColor(candidate.atsScore);
  const atsBadge  = getAtsMatchBadgeClass(candidate.atsMatchLabel);
  const avatarBg  = getAvatarColor(candidate.name);
  const initials  = getInitials(candidate.firstName, candidate.lastName);
  const hasResume = candidate.resumeUrl && candidate.resumeUrl !== "#";
  const canPreviewResume = hasResume && isPdfResumeUrl(candidate.resumeUrl, candidate.resumeFileName);

  /* ── Handlers ────────────────────────────────────────── */

  const setField = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    setFormErrors((p) => (p[k] ? { ...p, [k]: undefined } : p));
  };

  const handleStartEdit = () => {
    const snapshot = buildForm(candidate);
    setForm(snapshot);
    setInitialForm(snapshot);
    setFormErrors({});
    clearResumeFile();
    setActiveTab("overview");
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setForm(buildForm(candidate));
    setFormErrors({});
    clearResumeFile();
    setIsEditing(false);
  };

  const openResumePicker = () => document.getElementById(RESUME_INPUT_ID)?.click();

  const clearResumeFile = () => {
    setResumeFile(null);
    setResumeError("");
    const inp = document.getElementById(RESUME_INPUT_ID);
    if (inp) inp.value = "";
  };

  const handleResumeFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isPdfResumeFile(file)) {
      setResumeError("Only PDF files are allowed");
      setResumeFile(null);
      e.target.value = "";
      return;
    }
    setResumeError("");
    setResumeFile(file);
  };

  // Non-mandatory fields: omitted entirely if untouched, or [apiKey, value] where
  // value is the real string if changed, or `null` if the user cleared it out.
  const getChangedNonMandatoryEntries = () => {
    const entries = [];
    Object.entries(NON_MANDATORY_FIELD_MAP).forEach(([formKey, apiKey]) => {
      const current  = (form[formKey] || "").trim();
      const original = (initialForm[formKey] || "").trim();
      if (current === original) return;
      entries.push([apiKey, current === "" ? null : current]);
    });
    return entries;
  };

  const handleSave = async () => {
    if (!jobId || !candidate.id) {
      toast.error("Missing job reference. Candidate could not be updated.");
      return;
    }

    const errs = validateEditForm(form);
    setFormErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error("Please fix the highlighted fields before saving.");
      return;
    }

    const changedEntries = getChangedNonMandatoryEntries();
    // Without HIRING_CANDIDATE_STATUS_CHANGE, always resubmit the original
    // status regardless of form state, so the field can't be edited via the
    // Edit Candidate page even if the disabled control is bypassed client-side.
    const statusToSave = canChangeCandidateStatus ? form.status : candidate.status;

    setIsSavingResume(true);
    try {
      let res;
      if (resumeFile) {
        // A file forces multipart/form-data — FormData can't carry a real
        // `null`, so a cleared field is sent as the literal string "null" here.
        const fd = new FormData();
        fd.append("first_name", form.firstName.trim());
        fd.append("last_name", form.lastName.trim());
        fd.append("email", form.email.trim());
        fd.append("mobile", form.mobile.trim());
        changedEntries.forEach(([apiKey, value]) => fd.append(apiKey, value === null ? "null" : value));
        fd.append("status", statusToSave);
        fd.append("resume", resumeFile, resumeFile.name);
        res = await updateHiringApplication(jobId, candidate.id, fd);
      } else {
        // No file — plain JSON body. Untouched fields are omitted from the
        // object entirely; a field the user cleared is sent as a real `null`.
        const payload = {
          first_name: form.firstName.trim(),
          last_name:  form.lastName.trim(),
          email:      form.email.trim(),
          mobile:     form.mobile.trim(),
          status:     statusToSave,
        };
        changedEntries.forEach(([apiKey, value]) => { payload[apiKey] = value; });
        res = await updateHiringApplicationJson(jobId, candidate.id, payload);
      }

      const body = await res.json();
      if (!body.status) throw new Error(body.detail || body.message || "Failed to update candidate");
      setCandidate(normalizeApplication(body.data));
      clearResumeFile();
      toast.success("Candidate updated successfully");
      setIsEditing(false);
    } catch (err) {
      toast.error(err.message || "Failed to update candidate. Please try again.");
    } finally {
      setIsSavingResume(false);
    }
  };

  const handleDownload = () => {
    if (!hasResume) { toast("No resume available in demo mode"); return; }
    const a    = document.createElement("a");
    a.href     = candidate.resumeUrl;
    a.download = `${(candidate.name || "Resume").replace(/\s+/g, "_")}_Resume.pdf`;
    a.rel      = "noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  /* ── Overview tab ─────────────────────────────────────── */

  const renderOverview = () => {
    if (isEditing) return renderEditForm();

    const InfoCard = ({ title, Icon: TitleIcon, children }) => (
      <div className="cd-info-card">
        <p className="cd-info-card-title">
          {TitleIcon && <TitleIcon size={13} style={{ marginRight: 5 }} />}
          {title}
        </p>
        <div className="cd-info-card-grid">{children}</div>
      </div>
    );

    const Field = ({ label, value }) => (
      <div className="cd-info-field">
        <span className="cd-info-field-label">{label}</span>
        <span className="cd-info-field-value">{value || "—"}</span>
      </div>
    );

    const FullField = ({ label, children }) => (
      <div className="cd-info-field" style={{ gridColumn: "1 / -1" }}>
        <span className="cd-info-field-label">{label}</span>
        {children}
      </div>
    );

    const breakdown  = candidate.atsBreakdown || {};
    const strengths  = breakdown.strengths  || [];
    const weaknesses = breakdown.weaknesses || [];
    const matchedSkills  = breakdown.matchedSkills  ? breakdown.matchedSkills.split(", ").filter(Boolean)  : [];
    const missingSkills  = breakdown.missingSkills  ? breakdown.missingSkills.split(", ").filter(Boolean)  : [];
    const hasAiAssessment = !!(
      candidate.atsScoreReason || candidate.aiRecommendation || breakdown.matchLevel ||
      strengths.length || weaknesses.length || matchedSkills.length || missingSkills.length
    );

    return (
      <div className="cd-overview">
        <InfoCard title="Personal Information" Icon={MdPerson}>
          <Field label="First Name" value={candidate.firstName} />
          <Field label="Last Name"  value={candidate.lastName} />
          <Field label="Email"      value={candidate.email} />
          <Field label="Mobile"     value={candidate.mobile} />
          <Field label="Location"   value={candidate.currentLocation} />
        </InfoCard>

        <InfoCard title="Professional Information" Icon={MdWorkOutline}>
          <Field label="Experience"      value={candidate.totalExperience} />
          <Field label="Current Company" value={candidate.currentCompany} />
          <Field label="Current CTC"     value={candidate.currentCTC} />
          <Field label="Expected CTC"    value={candidate.expectedCTC} />
          <Field label="Notice Period"   value={candidate.noticePeriod} />
        </InfoCard>

        <InfoCard title="Application Information" Icon={MdAssignment}>
          <div className="cd-info-field">
            <span className="cd-info-field-label">ATS Score</span>
            <span style={{ fontSize: 14, color: atsColor, fontWeight: 600 }}>
              {candidate.atsScore}%
            </span>
          </div>
          <Field label="Status" value={candidate.status ? STATUS_LABEL[candidate.status] || candidate.status : ""} />
          <Field label="Source"       value={candidate.applicationSource} />
          <Field label="Applied Date" value={formatDate(candidate.appliedDate)} />
        </InfoCard>

        {hasAiAssessment && (
          <InfoCard title="AI Assessment" Icon={MdInsights}>
            {breakdown.matchLevel && <Field label="Match Level" value={breakdown.matchLevel} />}

            {candidate.atsScoreReason && (
              <FullField label="Score Reason">
                <p className="cd-ats-text">{candidate.atsScoreReason}</p>
              </FullField>
            )}

            {candidate.aiRecommendation && (
              <FullField label="Recommendation">
                <p className="cd-ats-text">{candidate.aiRecommendation}</p>
              </FullField>
            )}

            {strengths.length > 0 && (
              <FullField label="Strengths">
                <ul className="cd-ats-list">
                  {strengths.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </FullField>
            )}

            {weaknesses.length > 0 && (
              <FullField label="Weaknesses">
                <ul className="cd-ats-list">
                  {weaknesses.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </FullField>
            )}

            {matchedSkills.length > 0 && (
              <FullField label="Matched Skills">
                <div className="cd-skill-chip-list">
                  {matchedSkills.map((s, i) => (
                    <span key={i} className="cd-skill-chip cd-skill-chip--matched">{s}</span>
                  ))}
                </div>
              </FullField>
            )}

            {missingSkills.length > 0 && (
              <FullField label="Missing Skills">
                <div className="cd-skill-chip-list">
                  {missingSkills.map((s, i) => (
                    <span key={i} className="cd-skill-chip cd-skill-chip--missing">{s}</span>
                  ))}
                </div>
              </FullField>
            )}
          </InfoCard>
        )}
      </div>
    );
  };

  /* ── Edit form ─────────────────────────────────────────── */

  const renderEditForm = () => {
    return (
      <div className="cd-edit-form">
        <p className="cd-edit-section-title">
          <MdPerson size={13} style={{ marginRight: 5 }} />Personal Information
        </p>
        <div className="cd-edit-grid">
          <EditField k="firstName"       label="First Name" form={form} setField={setField} error={formErrors.firstName} />
          <EditField k="lastName"        label="Last Name" form={form} setField={setField} error={formErrors.lastName} />
          <EditField k="email"           label="Email" type="email" form={form} setField={setField} error={formErrors.email} />
          <EditField k="mobile"          label="Mobile Number" form={form} setField={setField} error={formErrors.mobile} />
          <div className="cd-edit-full">
            <EditField k="currentLocation" label="Current Location" form={form} setField={setField} error={formErrors.currentLocation} />
          </div>
        </div>

        <p className="cd-edit-section-title" style={{ marginTop: 24 }}>
          <MdWorkOutline size={13} style={{ marginRight: 5 }} />Professional Information
        </p>
        <div className="cd-edit-grid">
          <EditField k="totalExperience" label="Total Experience" form={form} setField={setField} error={formErrors.totalExperience} />
          <EditField k="currentCompany"  label="Current Company" form={form} setField={setField} error={formErrors.currentCompany} />
          <EditField k="currentCTC"      label="Current CTC" form={form} setField={setField} error={formErrors.currentCTC} />
          <EditField k="expectedCTC"     label="Expected CTC" form={form} setField={setField} error={formErrors.expectedCTC} />
          <EditField k="noticePeriod"    label="Notice Period" form={form} setField={setField} error={formErrors.noticePeriod} />
        </div>

        <p className="cd-edit-section-title" style={{ marginTop: 24 }}>
          <MdUploadFile size={13} style={{ marginRight: 5 }} />Resume
        </p>
        <div className="add-candidate-resume-field">
          <div
            className="add-candidate-file-picker"
            onClick={openResumePicker}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && openResumePicker()}
          >
            <input
              id={RESUME_INPUT_ID}
              type="file"
              accept={RESUME_ACCEPT}
              style={{ display: "none" }}
              onChange={handleResumeFileChange}
            />
            <BsCloudUpload
              style={{
                fontSize: "15px",
                color: resumeFile ? primaryColor : "#9ca3af",
                flexShrink: 0,
              }}
            />
            <span className={resumeFile ? "add-candidate-file-name" : "add-candidate-file-placeholder"}>
              {resumeFile
                ? resumeFile.name
                : (candidate.resumeFileName || (hasResume ? "Current resume on file — click to replace (PDF only)" : "Click to select resume (PDF only)"))}
            </span>
            {resumeFile && (
              <button
                type="button"
                className="add-candidate-file-clear-btn"
                title="Remove file"
                onClick={(e) => {
                  e.stopPropagation();
                  clearResumeFile();
                }}
              >
                ×
              </button>
            )}
          </div>
          {resumeError && <ErrorText>{resumeError}</ErrorText>}
        </div>

        <p className="cd-edit-section-title" style={{ marginTop: 24 }}>
          <MdAssignment size={13} style={{ marginRight: 5 }} />Application Status
        </p>
        <div className="cd-edit-grid">
          <div>
            <p className="cd-edit-ats-label">ATS Score</p>
            <p className="cd-edit-ats-value">
              {candidate.atsScore}%{" "}
              <span style={{ fontSize: 11, color: "#bdbdbd" }}>(read-only)</span>
            </p>
          </div>
          {!canChangeCandidateStatus ? (
            <div>
              <p className="cd-edit-ats-label">Status</p>
              <p className="cd-edit-ats-value">
                {STATUS_LABEL[form.status] || form.status}{" "}
                <span style={{ fontSize: 11, color: "#bdbdbd" }}>(read-only)</span>
              </p>
            </div>
          ) : (
            <StyledSelectGroup>
              <StyledSelectContainer>
                <StyledSelect
                  width="100%"
                  value={form.status}
                  onChange={(e) => setField("status", e.target.value)}
                >
                  <option value="APPLIED">Applied</option>
                  <option value="SHORTLISTED">Shortlisted</option>
                  <option value="HIRED">Hired</option>
                  <option value="REJECTED">Rejected</option>
                </StyledSelect>
                <StyledLabel hasValue className="user-label">Status</StyledLabel>
              </StyledSelectContainer>
            </StyledSelectGroup>
          )}
        </div>

        <div className="cd-edit-footer-actions">
          <SecondaryButton onClick={handleCancelEdit} disabled={isSavingResume}>Cancel</SecondaryButton>
          <PrimaryButton onClick={handleSave} disabled={isSavingResume}>
            {isSavingResume ? "Saving..." : "Save Changes"}
          </PrimaryButton>
        </div>
      </div>
    );
  };

  /* ── Resume tab ─────────────────────────────────────────── */

  const renderResume = () => (
    <div className="cd-resume-tab">
      <div className="cd-resume-actions-bar">
        <p className="cd-resume-actions-label">Uploaded Resume</p>
      </div>

      {canPreviewResume ? (
        <div className="cd-resume-viewer">
          <iframe
            src={`${candidate.resumeUrl}#navpanes=0${canDownloadResume ? "" : "&toolbar=0"}`}
            title="Candidate Resume"
            className="cd-resume-iframe"
          />
        </div>
      ) : (
        <div className="cd-resume-empty">
          <div className="cd-resume-empty-icon">
            <MdUploadFile size={52} />
          </div>
          {hasResume ? (
            <>
              <p className="cd-resume-empty-title">Preview unavailable</p>
              <p className="cd-resume-empty-sub">
                Only PDF files can be viewed here. Replace the resume with a PDF to preview it.
              </p>
            </>
          ) : (
            <>
              <p className="cd-resume-empty-title">No Resume Available</p>
              <p className="cd-resume-empty-sub">
                No resume was submitted with this application.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );

  /* ── JSX ──────────────────────────────────────────────────── */

  return (
    <>
      <Header />
      <div className="d-flex" style={{ "--hiring-primary": primaryColor }}>
        <LeftNavBar />
        <div className="page-content-wrapper hiring-detail-fade-in">

          {/* ── Page Header ── */}
          <PageHeader>
            <PageHeaderName>
              <ArrowLeft
                size={20}
                onClick={handleBack}
                style={{ cursor: "pointer", marginRight: 8, flexShrink: 0 }}
              />
              {jobTitle || "Hiring"}
            </PageHeaderName>

            <div style={{ display: "flex", alignItems: "center", gap: 10, marginRight: 20 }}>
              <span
                className="cd-header-status"
                style={{
                  background: getStatusBg(candidate.status, primaryColor),
                  color: getStatusText(candidate.status, primaryColor),
                }}
              >
                {candidate.status}
              </span>
              <span
                className="cd-header-ats"
                style={{ borderColor: atsColor, color: atsColor }}
              >
                {candidate.atsScore}% ATS
              </span>
              {candidate.atsMatchLabel && (
                <span className={atsBadge}>{candidate.atsMatchLabel}</span>
              )}
              {!isEditing && (
                <>
                  {canDownloadResume && (
                    <SecondaryButton
                      onClick={handleDownload}
                      style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}
                    >
                      <BsDownload size={13} /> Download
                    </SecondaryButton>
                  )}
                  {!jobClosed && canEditCandidate && (
                    <PrimaryButton onClick={handleStartEdit}>Edit Candidate</PrimaryButton>
                  )}
                </>
              )}
            </div>
          </PageHeader>

          {/* ── Summary Card ── */}
          <div className="cd-summary-card">

            {/* Profile row: avatar + name/subtitle/date */}
            <div className="cd-profile-row">
              <div className="cd-avatar" style={{ background: avatarBg }}>{initials}</div>
              <div className="cd-profile-info">
                <h1 className="cd-candidate-name">{candidate.name}</h1>
                {jobTitle && <p className="cd-job-sub">{jobTitle}</p>}
                <p className="cd-applied-meta">
                  {candidate.appliedDate && `Applied ${formatDate(candidate.appliedDate)}`}
                  {candidate.applicationSource && ` · via ${candidate.applicationSource}`}
                </p>
              </div>
            </div>

            {/* 4-card stat strip — mirrors Job Details page */}
            <div className="cd-stat-strip">
              <div className="cd-stat-item">
                <span className="cd-stat-status-chip"
                  style={{
                    background: getStatusBg(candidate.status, primaryColor),
                    color: getStatusText(candidate.status, primaryColor),
                  }}
                >
                  {candidate.status}
                </span>
                <span className="cd-stat-label">Status</span>
              </div>
              <div className="cd-stat-item">
                <span className="cd-stat-value">{candidate.totalExperience || "—"}</span>
                <span className="cd-stat-label">Experience</span>
              </div>
              <div className="cd-stat-item">
                <span className="cd-stat-value">{candidate.currentCTC || "—"}</span>
                <span className="cd-stat-label">Current CTC</span>
              </div>
              <div className="cd-stat-item">
                <span className="cd-stat-value">{candidate.expectedCTC || "—"}</span>
                <span className="cd-stat-label">Expected CTC</span>
              </div>
            </div>

          </div>

          {/* ── Content Box with Tabs ── */}
          <div className="cd-content-box">
            <div className="cd-tab-bar">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  className={activeTab === tab.id ? "cd-tab cd-tab--active" : "cd-tab"}
                  style={
                    activeTab === tab.id
                      ? { color: primaryColor, borderBottomColor: primaryColor }
                      : {}
                  }
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div key={activeTab} className="cd-tab-content hiring-tab-content-fade">
              {activeTab === "resume"   && renderResume()}
              {activeTab === "overview" && renderOverview()}
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default CandidateDetailsPage;
